// How pick'em works, in three steps (scenario I6, B-484,
// PLAN-2026-10-23-pickem-that-explains-itself §4): the words behind the info
// button on the League page's card and the screen's title, and a panel of its
// own on the screen. One copy, so the two never say different things.

import { InfoButton } from '../../kit/InfoButton';
import { Stack, Text } from '../../kit/ui';
import { fmtWhen } from './RosterMoveSheet';

/** What the second step names: the week open to pick and its lock. */
export interface PickemLock {
  label: string;
  at: string;
}

/** The lock to name, from a pick'em read: its week's, while that week is still open. */
export function lockToName(view: {
  period: { label: string };
  locksAt: string | null;
  locked: boolean;
}): PickemLock | null {
  return view.locked || view.locksAt === null
    ? null
    : { label: view.period.label, at: view.locksAt };
}

/** The steps. `lock` names the open week's lock in the second, when there is one. */
export function PickemSteps({ lock, zone }: { lock: PickemLock | null; zone: string }) {
  return (
    <Stack gap={3}>
      <ol className="fsp-pickem-steps">
        <li>
          <Text weight="strong" block>
            Pick a winner for every game.
          </Text>
          <Text size="sm" tone="secondary" block>
            Each week, choose the team you think will win each of your league’s games, yours
            included.
          </Text>
        </li>
        <li>
          <Text weight="strong" block>
            Change your mind until the lock.
          </Text>
          <Text size="sm" tone="secondary" block>
            Picks save as you go. They lock at the week’s first kickoff
            {lock !== null ? ` (${lock.label}’s, ${fmtWhen(lock.at, zone)})` : ''}, and then
            everybody’s picks are shown.
          </Text>
        </li>
        <li>
          <Text weight="strong" block>
            Score points.
          </Text>
          <Text size="sm" tone="secondary" block>
            A right pick is a point and a tie half a point. The table adds up the season, and the
            top picker wins the Best Picker award.
          </Text>
        </li>
        <li>
          <Text weight="strong" block>
            Guess your score for a Bullseye.
          </Text>
          <Text size="sm" tone="secondary" block>
            Each week, guess your own team’s score before the lock. The guess nearest its team’s
            final score wins the week’s Bullseye, and a tie on points goes to more right picks, then
            more bullseyes.
          </Text>
        </li>
      </ol>
      <Text size="sm" tone="secondary" block>
        Any bots in the league pick too, each in its own way. It’s for bragging rights: nothing to
        enter, and nothing to win but the award.
      </Text>
    </Stack>
  );
}

/** The ⓘ beside pick'em's name: the steps, when asked. */
export function PickemInfo({ lock, zone }: { lock: PickemLock | null; zone: string }) {
  return (
    <InfoButton label="How pick’em works" title="How pick’em works">
      <PickemSteps lock={lock} zone={zone} />
    </InfoButton>
  );
}
